import { motion, useInView } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { api } from "@/lib/api";

export default function ClientsCarousel() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [clients, setClients] = useState([]);

  useEffect(() => {
    api.getClients().then((r) => setClients(r.data)).catch(() => {});
  }, []);

  if (clients.length === 0) return null;

  // Duplicate for seamless infinite scroll
  const items = [...clients, ...clients, ...clients];

  return (
    <section
      ref={ref}
      className="py-20 md:py-28 bg-[#0A0A0A] relative overflow-hidden"
      data-testid="clients-section"
    >
      {/* Subtle grain */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E\")" }} />

      <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-20 mb-14 md:mb-20">
          <div className="md:col-span-2">
            <motion.p
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ delay: 0.2 }}
              className="text-[11px] font-semibold uppercase tracking-[0.25em] text-neutral-600"
            >
              Confianza
            </motion.p>
          </div>
          <div className="md:col-span-10">
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="text-3xl md:text-5xl lg:text-6xl font-normal text-white tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Nuestros Clientes
            </motion.h2>
          </div>
        </div>
      </div>

      {/* Infinite scroll strip */}
      <div className="relative">
        {/* Edge fades */}
        <div className="absolute left-0 top-0 bottom-0 w-32 z-10 bg-gradient-to-r from-[#0A0A0A] to-transparent pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-32 z-10 bg-gradient-to-l from-[#0A0A0A] to-transparent pointer-events-none" />

        <motion.div
          className="flex gap-8 md:gap-12"
          animate={{ x: [0, -(clients.length * 220)] }}
          transition={{ x: { repeat: Infinity, repeatType: "loop", duration: clients.length * 5, ease: "linear" } }}
        >
          {items.map((c, i) => (
            <div
              key={`${c.id}-${i}`}
              className="flex-shrink-0 w-[180px] md:w-[200px] group"
            >
              <div className="relative aspect-square rounded-2xl overflow-hidden border border-white/[0.06] bg-white/[0.03] backdrop-blur-sm p-6 flex items-center justify-center transition-all duration-500 group-hover:border-white/[0.15] group-hover:bg-white/[0.06] group-hover:shadow-[0_0_40px_rgba(255,255,255,0.05)]">
                {/* Shimmer sweep */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
                  <div style={{
                    background: "linear-gradient(105deg, transparent 38%, rgba(255,255,255,0.04) 44%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.04) 56%, transparent 62%)",
                    backgroundSize: "250% 100%",
                    animation: `shimmerSweep ${6 + (i % 3)}s ease-in-out infinite`,
                    animationDelay: `${(i % 5) * 0.8}s`,
                    position: "absolute", inset: 0,
                  }} />
                </div>
                {/* Logo */}
                <img
                  src={c.logo_url}
                  alt={c.name}
                  className="w-full h-full object-contain filter grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700"
                  loading="lazy"
                />
              </div>
              {/* Client name */}
              <p className="mt-3 text-center text-xs text-neutral-600 tracking-widest uppercase group-hover:text-neutral-400 transition-colors duration-500">
                {c.name}
              </p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
